import React, { useEffect, useState } from "react";
import {
  Link,
  NavLink,
  useLocation,
} from "react-router-dom";

import andUsLogo from "../../images/logolight.png";
import unityLearningIcon from "../../images/icon-unity-learning.svg";
import talentLenzIcon from "../../images/icon-talentlenz.svg";
import fleetCoreIcon from "../../images/icon-fleetcore.svg";
import workPassportIcon from "../../images/icon-work-passport.svg";

import "./Navbar.css";

const systems = [
  {
    name: "Unity Learning",
    description: "AI-powered learning",
    to: "/unity-learning",
    tone: "blue",
    icon: unityLearningIcon,
  },
  {
    name: "TalentLenz",
    description: "Evidence-based selection",
    to: "/talentlenz",
    tone: "pink",
    icon: talentLenzIcon,
  },
  {
    name: "FleetCore",
    description: "Intelligent fleet control",
    to: "/fleet-core",
    tone: "blue",
    icon: fleetCoreIcon,
  },
  {
    name: "Work Passport",
    description: "Workforce compliance",
    to: "/workpassport",
    tone: "pink",
    icon: workPassportIcon,
  },
];

function MenuIcon({ open }) {
  return open ? (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function ArrowIcon({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path d="M4 10h11" />
      <path d="m11 6 4 4-4 4" />
    </svg>
  );
}

function CustomSystemsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path d="M12 3.25 13.55 8l4.7 1.55-4.7 1.55L12 15.85l-1.55-4.75-4.7-1.55L10.45 8 12 3.25Z" />
      <path d="m18.2 14.4.75 2.25 2.25.75-2.25.75-.75 2.25-.75-2.25-2.25-.75 2.25-.75.75-2.25Z" />
    </svg>
  );
}

function ProductNavigationLink({ system, closeMenu }) {
  return (
    <NavLink
      end
      to={system.to}
      onClick={closeMenu}
      className={({ isActive }) =>
        [
          "luminous-navbar__system-link",
          `luminous-navbar__system-link--${system.tone}`,
          isActive ? "is-active" : "",
        ]
          .filter(Boolean)
          .join(" ")
      }
      aria-label={`Explore ${system.name}`}
    >
      <span className="luminous-navbar__system-icon">
        <img src={system.icon} alt="" aria-hidden="true" />
      </span>

      <span className="luminous-navbar__system-copy">
        <strong>{system.name}</strong>
        <small>{system.description}</small>
      </span>

      <ArrowIcon className="luminous-navbar__system-arrow" />
    </NavLink>
  );
}

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const systemsActive = systems.some(
    (system) =>
      location.pathname === system.to ||
      location.pathname.startsWith(`${system.to}/`)
  );

  const closeMenu = () => {
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth > 1120) {
        setMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  return (
    <header
      className={`luminous-navbar-shell ${
        menuOpen ? "menu-is-open" : ""
      }`}
    >
      <nav
        className="luminous-navbar"
        aria-label="Primary navigation"
      >
        <Link
          className="luminous-navbar__brand"
          to="/"
          aria-label="AndUs home"
          onClick={closeMenu}
        >
          <img src={andUsLogo} alt="AndUs" />
        </Link>

        <button
          type="button"
          className="luminous-navbar__toggle"
          aria-expanded={menuOpen}
          aria-controls="luminous-primary-menu"
          aria-label={
            menuOpen ? "Close navigation" : "Open navigation"
          }
          onClick={() => {
            setMenuOpen((current) => !current);
          }}
        >
          <span className="luminous-navbar__toggle-label">
            {menuOpen ? "Close" : "Menu"}
          </span>

          <span className="luminous-navbar__toggle-icon">
            <MenuIcon open={menuOpen} />
          </span>
        </button>

        <div
          id="luminous-primary-menu"
          className={`luminous-navbar__menu ${
            menuOpen ? "is-open" : ""
          }`}
        >
          <div className="luminous-navbar__mobile-intro">
            <span>Navigate AndUs</span>
            <strong>What would you like to explore?</strong>
          </div>

          <section
            className={`luminous-navbar__systems ${
              systemsActive ? "has-active-system" : ""
            }`}
            aria-labelledby="luminous-navbar-systems-title"
          >
            <div className="luminous-navbar__systems-heading">
              <span
                className="luminous-navbar__systems-dot"
                aria-hidden="true"
              />

              <span id="luminous-navbar-systems-title">
                SaaS systems
              </span>

              <small>Ready-to-deploy platforms</small>
            </div>

            <div className="luminous-navbar__systems-list">
              {systems.map((system) => (
                <ProductNavigationLink
                  key={system.name}
                  system={system}
                  closeMenu={closeMenu}
                />
              ))}
            </div>
          </section>

          <NavLink
            end
            to="/custom-systems"
            onClick={closeMenu}
            className={({ isActive }) =>
              `luminous-navbar__custom ${
                isActive ? "is-active" : ""
              }`
            }
          >
            <span className="luminous-navbar__custom-icon">
              <CustomSystemsIcon />
            </span>

            <span className="luminous-navbar__custom-copy">
              <strong>Custom Systems</strong>
              <small>Built around your operation</small>
            </span>

            <ArrowIcon className="luminous-navbar__custom-arrow" />
          </NavLink>

          <div className="luminous-navbar__company">
            <span className="luminous-navbar__company-label">
              Company
            </span>

            <NavLink
              end
              to="/about-andus"
              onClick={closeMenu}
              className={({ isActive }) =>
                `luminous-navbar__about ${
                  isActive ? "is-active" : ""
                }`
              }
            >
              <span>About &amp;Us</span>

              <ArrowIcon className="luminous-navbar__about-arrow" />
            </NavLink>
          </div>

          <NavLink
            end
            className={({ isActive }) =>
              `luminous-navbar__contact ${
                isActive ? "is-active" : ""
              }`
            }
            to="/contactAndUs"
            onClick={closeMenu}
          >
            <span
              className="luminous-navbar__contact-status"
              aria-hidden="true"
            />

            <span>Contact &amp;Us</span>

            <ArrowIcon className="luminous-navbar__contact-arrow" />
          </NavLink>
        </div>
      </nav>

      <button
        type="button"
        className={`luminous-navbar__backdrop ${
          menuOpen ? "is-visible" : ""
        }`}
        aria-label="Close navigation"
        tabIndex={-1}
        onClick={closeMenu}
      />
    </header>
  );
}